// ============================================================
// settlement.types.ts — Stage 4 round 2a (PI settlement inputs + preview).
// Mirrors backend lib/settlementInputs.ts + controllers/settlementController.
// Money is integer PENCE, rates BASIS POINTS (2500 = 25%), dates "YYYY-MM-DD".
// ============================================================
import type { Representatives } from './settlementStatement.types'
export type CfaStage = 'PRE_ISSUE' | 'POST_ISSUE' | 'LISTED_FOR_TRIAL' | 'TRIAL' | 'APPEAL'
export type DeductionItem = 'SUCCESS_FEE' | 'COUNSEL_SUCCESS_FEE' | 'SHORTFALL' | 'ATE_PREMIUM' | 'DISBURSEMENTS'
export type DeductionCapBase = 'DAMAGES_TOTAL' | 'DAMAGES_NET_OF_CRU' | 'PSLA_AND_PAST_LOSS_NET_OF_CRU'
export type ProceedingsStage = 'PRE_ISSUE' | 'ISSUED_COUNTY_COURT' | 'ISSUED_HIGH_COURT'
export type ProceedingsLevel = 'FIRST_INSTANCE' | 'APPEAL'
export type RecoveryStatus = 'PENDING' | 'RECOVERED' | 'PART_RECOVERED' | 'IRRECOVERABLE'
// (B4) CHILD_ACCOUNT — paid into an account in the child's own name.
export type NetDestination = 'CLIENT' | 'COURT_FUNDS_OFFICE' | 'DEPUTY' | 'CHILD_ACCOUNT'

export interface StageRate { stage: CfaStage; rateBasisPoints: number }

export interface MatterValues {
  pslaPence: number
  // Past loss by head (CRU is checked like-for-like); total = the four.
  pastLossEarningsPence: number; pastLossCarePence: number; pastLossMobilityPence: number; pastLossOtherPence: number
  futureLossPence: number; interestPence: number; quantumDate: string | null
  cruPastLossEarningsPence: number; cruPastCarePence: number; cruPastMobilityPence: number; cruCertificateDate: string | null
  proceedingsStage: ProceedingsStage; proceedingsLevel: ProceedingsLevel; issuedOn: string | null; court: string
  // The stage the claim has reached — picks the CFA's staged success rate.
  stageReached: CfaStage
  recoveredCostsPence: number; recoveredCostsVatPence: number; recoveredCostsAgreedOn: string | null
  ateProvider: string; atePremiumPence: number; ateRecoveredPence: number; ateCommissionPence: number | null; ateCommissionDisclosed: boolean
  counselSuccessFeePence: number; adverseCostsOffsetPence: number; protectedParty: boolean
}

export interface CfaTerms {
  signedOn: string | null
  successFeeOnRecoveredCostsOnly: boolean
  capIncludesVat: boolean
  capBaseIncludesInterest: boolean
  counselInCap: boolean
  shortfallClause: boolean
  s74_3Clause: boolean
  ateDeductible: boolean
  noDeductionsFromChildren: boolean
  totalCapBasisPoints: number | null
  totalCapBase: DeductionCapBase
  totalCapReduceOrder: DeductionItem[]
  riskAssessmentNote: string
  stages: StageRate[]
}
export interface CfaRecord extends CfaTerms {
  id: string
  source: 'MANUAL' | 'MIGRATED'
  needsReview: boolean
  reviewedAt: string | null
  reviewedBy: string | null
  legacyLabel: string | null
  wordingVersion: string
  outsetDeductionEstimateBasisPoints: number | null
  updatedAt: string
}
export type CfaEditable = CfaTerms & { outsetDeductionEstimateBasisPoints: number | null; wordingVersion: string }

export interface InterimPayment { id: string; amountPence: number; receivedOn: string | null; payer: string; note: string }
export interface SettlementDisbursement {
  id: string; description: string; category: string; status: string; amountPence: number; vatPence: number; payableTo: string
  paidAt: string | null; invoiceId: string | null
  recoveryStatus: RecoveryStatus; recoveredPence: number; recoveredVatPence: number; recoveredAt: string | null; unrecoveredPence: number
  // (Audit batch 3) On a settled, delivered interim bill (its number) → left
  // out of the deductions; the card lists it muted and doesn't count it.
  alreadyBilledOn?: string | null
}
export interface BaseCosts {
  netPence: number; vatPence: number; entryCount: number
  byFeeEarner: { userId: string; name: string; entryCount: number; seconds: number; netPence: number }[]
}
export interface BaseCostsOverride { netPence: number; vatPence: number; reason: string }
// (B4 follow-up) One card, one request (PUT /settlement/costs | /settlement/ate).
export type SettlementCardKind = 'costs' | 'ate'
export interface SettlementCardSave {
  matter?: Partial<MatterValues>
  // (Audit fix batch 1, H4) REQUIRED: the matter's version as the card opened
  // (null = no matter yet) — whatever the card changed.
  baseUpdatedAt: string | null
  // Costs card only.
  override?: { netPence: number; vatPence: number; reason: string } | { clear: true }
  // ATE card only.
  recoveries?: ({ id: string } & Partial<{ recoveryStatus: RecoveryStatus; recoveredPence: number; recoveredVatPence: number; recoveredAt: string | null }>)[]
}
export interface SettlementCardSaved {
  values: MatterValues; updatedAt: string | null; baseCostsOverride: BaseCostsOverride | null; disbursements: SettlementDisbursement[]
}

export type SettlementState =
  | { locked: true; reason: string }
  | {
      locked: false
      matter: {
        exists: boolean; updatedAt: string | null; values: MatterValues
        adultConfirmedAt: string | null; adultConfirmedBy: string | null
        baseCostsOverride: BaseCostsOverride | null
      }
      // dateOfBirth = THE DOB this case uses (its own copy, else the client record);
      // clientRecordDob = the client record's when it DIFFERS (else null).
      // caseDobUnreadable = the case's own copy is there but can't be read (it
      // counts as blank, so the client record's is used).
      client: { dateOfBirth: string | null; dobValid: boolean; childToday: boolean | null; clientRecordDob?: string | null; caseDobUnreadable?: boolean }
      // The case's accident date (YYYY-MM-DD) or null — the CFA card's date check.
      accidentDate: string | null
      cfa: CfaRecord | null
      interims: InterimPayment[]
      disbursements: SettlementDisbursement[]
      baseCosts: BaseCosts
      today: string
      // (B4 follow-up) The litigation friend + deputy / attorney (PiMatter).
      representatives?: Representatives
      // (Batch 2) A draft statement that is PREPARED or APPROVED locks the
      // figures it uses: the cards are read-only until it is sent back.
      statementLock?: { statementId: string; version: number; stage: 'PREPARED' | 'APPROVED' } | null
    }

export interface Issue { code: string; message: string }
export interface WaterfallRow {
  key: string; label: string; section: 'DAMAGES' | 'LESS' | 'CAP' | 'DEDUCTION' | 'TOTAL'; rule: string
  amountPence: number; reductions: { rule: string; pence: number }[]; finalPence: number
}
export interface SettlementFigures {
  totalDamagesPence: number; capBasePence: number; statutoryCapPence: number; rawSuccessFeePence: number; successFeePence: number
  counselSuccessFeeChargedPence: number; shortfallPence: number; atePence: number; disbursementsPence: number; adverseCostsOffsetPence: number
  totalBeforeFirmCapPence: number; firmCapPence: number | null; totalDeductionsPence: number; interimPaymentsPence: number
  retentionPence: number; netToClientPence: number
}
export interface PreviewOptions {
  settlementDate?: string; kind?: 'INTERIM' | 'FINAL'; retentionPence?: number; shortfallWaived?: boolean; netDestination?: NetDestination
}
export interface PreviewEdits {
  matter?: Partial<MatterValues>
  cfa?: Partial<CfaTerms>
  baseCostsOverride?: { netPence: number; vatPence: number } | null
  disbursements?: Record<string, { recoveredPence?: number; recoveredVatPence?: number }>
  interimPaymentsPence?: number[]
}
export interface PreviewResult {
  ok: boolean
  errors: Issue[]
  warnings: Issue[]
  used: {
    cfaStage: CfaStage
    rate: { rateBasisPoints: number; fromStage: CfaStage | null }
    baseCosts: { netPence: number; vatPence: number; source: 'recorded' | 'override' }
    cruTotalPence: number // as certified (all heads)
    cruAppliedPence: number // offset: each head capped at its matching loss
    unrecoveredDisbursementsPence: number
    settlementDate: string
  }
  result: null | {
    engineVersion: string
    capacity: 'ADULT' | 'CHILD' | 'PROTECTED_PARTY'
    deductionsProposed: boolean
    issueBlocked: Issue | null
    cpr2112Band: 'UP_TO_5000' | 'UP_TO_25000' | 'OVER_25000' | null
    figures: SettlementFigures
    rows: WaterfallRow[]
  }
}

export interface SettlementDefaults {
  defaultSuccessRateBasisPoints: number | null
  defaultSuccessStages: StageRate[]
  defaultSuccessFeeOnRecoveredCostsOnly: boolean
  defaultCapIncludesVat: boolean
  defaultCapBaseIncludesInterest: boolean
  defaultCounselInCap: boolean
  defaultShortfallClause: boolean
  defaultS74_3Clause: boolean
  defaultAteDeductible: boolean
  defaultNoDeductionsFromChildren: boolean
  defaultTotalCapBasisPoints: number | null
  // (Batch 2) Chase the client’s answer to an issued statement after N days (1–90).
  statementChaseDays?: number
  defaultTotalCapBase: DeductionCapBase
  defaultTotalCapReduceOrder: DeductionItem[]
}

// ── Labels shared by Admin › PI settlements and the case's CFA panel ──
export const CFA_STAGE_LABEL: Record<CfaStage, string> = {
  PRE_ISSUE: 'Before issue', POST_ISSUE: 'After issue', LISTED_FOR_TRIAL: 'Listed for trial', TRIAL: 'At trial', APPEAL: 'Appeal',
}
export const CFA_STAGES: CfaStage[] = ['PRE_ISSUE', 'POST_ISSUE', 'LISTED_FOR_TRIAL', 'TRIAL', 'APPEAL']
export const DEDUCTION_LABEL: Record<DeductionItem, string> = {
  SUCCESS_FEE: 'Success fee', COUNSEL_SUCCESS_FEE: 'Counsel’s success fee', SHORTFALL: 'Base-costs shortfall',
  ATE_PREMIUM: 'ATE premium', DISBURSEMENTS: 'Unrecovered disbursements',
}
export const CAP_BASE_LABEL: Record<DeductionCapBase, string> = {
  DAMAGES_TOTAL: 'All damages',
  DAMAGES_NET_OF_CRU: 'All damages, less CRU',
  PSLA_AND_PAST_LOSS_NET_OF_CRU: 'General damages + past loss, less CRU',
}
// The CFA terms' one-line explanations — and, for each Yes/No term, which
// answer deducts LESS from the client (shown as "deducts less").
export interface TermSpec { key: keyof CfaTerms & string; title: string; desc: string; lessIs: boolean }
export const CFA_TERM_SPECS: TermSpec[] = [
  { key: 'successFeeOnRecoveredCostsOnly', title: 'Success fee on recovered costs only', desc: 'Work out the success fee on the costs the other side actually paid, not on all the time recorded.', lessIs: true },
  { key: 'capIncludesVat', title: 'The 25% cap includes VAT', desc: 'Measure the success fee against the statutory cap with its VAT included, so VAT can’t take it over.', lessIs: true },
  { key: 'capBaseIncludesInterest', title: 'Interest counts towards the cap', desc: 'Include interest on damages in the figure the 25% cap is a percentage of.', lessIs: false },
  { key: 'counselInCap', title: 'Pass on counsel’s success fee', desc: 'Charge the client counsel’s success fee too, inside the same 25% cap.', lessIs: false },
  { key: 'shortfallClause', title: 'Shortfall clause', desc: 'Let the firm charge the client the difference between its base costs and the costs recovered.', lessIs: false },
  { key: 's74_3Clause', title: 's.74(3) agreement', desc: 'In County Court cases, let the firm charge more than the court allowed (Solicitors Act 1974 s.74(3)). Without it, no shortfall is charged there.', lessIs: false },
  { key: 'ateDeductible', title: 'Deduct the ATE premium', desc: 'Take the part of the ATE insurance premium the other side didn’t pay out of damages.', lessIs: false },
  { key: 'noDeductionsFromChildren', title: 'No deductions from children', desc: 'Deduct nothing from a child’s or protected party’s damages.', lessIs: true },
]
export const bpToPercentText = (bp: number | null | undefined): string => (bp == null ? '' : String(bp / 100))
// "25" / "12.5" → 2500 / 1250; blank → null; anything else → undefined (invalid).
export const percentTextToBp = (raw: string): number | null | undefined => {
  const s = raw.trim()
  if (!s) return null
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(s)) return undefined
  const [w, f = ''] = s.split('.')
  const bp = Number(w) * 100 + Number((f + '00').slice(0, 2))
  return bp <= 10000 ? bp : undefined
}
